import { memo, useMemo } from 'react';

import { type ElementInfo } from '~/components/DevModal/types';
import { parseCSSValue } from './utils';

type GapVisualizationProps = {
  elementInfo: ElementInfo;
};

export const GapVisualization = memo(({ elementInfo }: GapVisualizationProps) => {
  // Memoize gap calculations
  const gapValues = useMemo(() => {
    const gap = parseCSSValue(elementInfo.styles.gap || '0');
    const columnGap = parseCSSValue(elementInfo.styles.columnGap || '0');
    const rowGap = parseCSSValue(elementInfo.styles.rowGap || '0');
    return {
      gap,
      columnGap,
      rowGap,
      effectiveGap: Math.max(gap, columnGap, rowGap),
    };
  }, [elementInfo.styles.gap, elementInfo.styles.columnGap, elementInfo.styles.rowGap]);

  // Only show gap visualization if we have a gap value
  if (gapValues.effectiveGap <= 0) {
    return null;
  }

  // Get the actual DOM element to find child elements
  let targetElement = document.elementFromPoint(elementInfo.position.x + 10, elementInfo.position.y + 10);

  if (!targetElement) {
    return null;
  }

  // If we found an SVG element or element with no children, walk up to find the flex container
  if (
    targetElement &&
    (targetElement.tagName === 'path' || targetElement.tagName === 'svg' || targetElement.children.length === 0)
  ) {
    let currentElement = targetElement;

    // Walk up to find an element with children and flex display
    while (currentElement && currentElement !== document.body) {
      const computedStyle = window.getComputedStyle(currentElement);
      if (
        (computedStyle.display === 'flex' || computedStyle.display === 'grid') &&
        currentElement.children.length > 0
      ) {
        targetElement = currentElement;
        break;
      }
      const parentElement = currentElement.parentElement;
      if (!(parentElement instanceof HTMLElement)) break;
      currentElement = parentElement;
    }
  }

  // Check if we have a valid target element
  if (!targetElement) {
    return null;
  }

  // At this point, targetElement is guaranteed to be non-null
  const element = targetElement;

  // Get child elements and their positions
  const children = Array.from(element.children);

  if (children.length === 0) {
    return null;
  }

  // Check for flex spacing methods
  const computedStyle = window.getComputedStyle(element);
  const justifyContent = computedStyle.justifyContent;

  // Check if children have margins that create spacing
  const childMargins = children.map((child) => {
    const childStyle = window.getComputedStyle(child);
    return {
      marginLeft: parseCSSValue(childStyle.marginLeft),
      marginRight: parseCSSValue(childStyle.marginRight),
      marginTop: parseCSSValue(childStyle.marginTop),
      marginBottom: parseCSSValue(childStyle.marginBottom),
    };
  });

  const childPositions = children.map((child) => {
    const rect = child.getBoundingClientRect();
    return {
      left: rect.left,
      right: rect.right,
      top: rect.top,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height,
    };
  });

  // Find gaps between children
  const gaps = [];
  for (let i = 0; i < childPositions.length - 1; i++) {
    const currentChild = childPositions[i];
    const nextChild = childPositions[i + 1];

    // Calculate gap between this child and the next
    const gapStart = currentChild.right;
    const gapEnd = nextChild.left;
    const gapWidth = gapEnd - gapStart;

    if (gapWidth > 0) {
      gaps.push({
        left: gapStart,
        top: Math.min(currentChild.top, nextChild.top),
        width: gapWidth,
        height: Math.max(currentChild.height, nextChild.height),
      });
    }
  }

  if (gaps.length === 0) {
    // Check if we have justify-content spacing or child margins
    let spacingInfo = '';

    if (justifyContent.includes('space')) {
      spacingInfo = `${justifyContent} spacing`;
    } else if (childMargins.some((m) => m.marginLeft > 0 || m.marginRight > 0)) {
      const maxMargin = Math.max(...childMargins.map((m) => Math.max(m.marginLeft, m.marginRight)));
      spacingInfo = `Child margins: ${maxMargin}px`;
    }

    if (spacingInfo) {
      return (
        <div className="pointer-events-none fixed z-[9999]">
          {/* Show flex spacing as a dashed border around the content area */}
          <div
            className="fixed border border-blue-5 border-dashed bg-blue-5/30"
            style={{
              left: elementInfo.position.x,
              top: elementInfo.position.y,
              width: elementInfo.dimensions.width,
              height: elementInfo.dimensions.height,
              zIndex: 10000,
            }}
          />
          {/* Add spacing label */}
          <div
            className="fixed rounded bg-blue-5 px-3 py-2 font-bold text-14 text-white shadow-xl"
            style={{
              left: elementInfo.position.x + 10,
              top: elementInfo.position.y - 35,
              zIndex: 10001,
            }}
          >
            {spacingInfo}
          </div>
        </div>
      );
    }

    // Calculate spacing between children based on flex properties
    const spacingElements = [];

    if (justifyContent.includes('space')) {
      // For justify-content spacing, show the spaces between children
      for (let i = 0; i < children.length - 1; i++) {
        const currentChild = childPositions[i];
        const nextChild = childPositions[i + 1];

        // Calculate the space between these children
        const spaceStart = currentChild.right;
        const spaceEnd = nextChild.left;
        const spaceWidth = spaceEnd - spaceStart;

        if (spaceWidth > 0) {
          spacingElements.push({
            left: spaceStart,
            top: Math.min(currentChild.top, nextChild.top),
            width: spaceWidth,
            height: Math.max(currentChild.height, nextChild.height),
            type: 'justify-content',
            label: `${justifyContent} space`,
          });
        }
      }
    } else if (childMargins.some((m) => m.marginLeft > 0 || m.marginRight > 0)) {
      // For child margins, show the margin areas
      childPositions.forEach((child, index) => {
        const margins = childMargins[index];
        if (margins.marginLeft > 0) {
          spacingElements.push({
            left: child.left - margins.marginLeft,
            top: child.top,
            width: margins.marginLeft,
            height: child.height,
            type: 'child-margin',
            label: `Left margin: ${margins.marginLeft}px`,
          });
        }
        if (margins.marginRight > 0) {
          spacingElements.push({
            left: child.right,
            top: child.top,
            width: margins.marginRight,
            height: child.height,
            type: 'child-margin',
            label: `Right margin: ${margins.marginRight}px`,
          });
        }
      });
    }

    if (spacingElements.length > 0) {
      return (
        <div className="pointer-events-none fixed z-[9999]">
          {/* Show each spacing element */}
          {spacingElements.map((spacing, index) => (
            <div key={index}>
              {/* Spacing visualization */}
              <div
                className="fixed border-2 border-blue-5 border-dashed bg-blue-5/40"
                style={{
                  left: spacing.left,
                  top: spacing.top,
                  width: spacing.width,
                  height: spacing.height,
                  zIndex: 10000,
                }}
              />
              {/* Spacing label */}
              <div
                className="fixed rounded bg-blue-5 px-2 py-1 font-bold text-12 text-white shadow-lg"
                style={{
                  left: spacing.left + spacing.width / 2 - 20,
                  top: spacing.top + spacing.height / 2 - 10,
                  zIndex: 10001,
                }}
              >
                {spacing.label}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // No gaps found, don't show anything
    return null;
  }

  return (
    <div className="pointer-events-none fixed z-[9999]">
      {/* Show each gap as a rectangular area */}
      {gaps.map((gap, index) => (
        <div key={index}>
          {/* Gap visualization - use fixed positioning relative to viewport */}
          <div
            className="fixed border border-purple-5 border-dashed bg-purple-5/50"
            style={{
              left: gap.left,
              top: gap.top,
              width: gap.width,
              height: gap.height,
              zIndex: 10000,
            }}
          />
        </div>
      ))}
    </div>
  );
});

GapVisualization.displayName = 'GapVisualization';
